import { I18n } from '@affine/i18n';
import { DefaultTool } from '@blocksuite/affine/blocks/surface';
import { menu } from '@blocksuite/affine/components/context-menu';
import { AIStarIcon } from '@blocksuite/affine/components/icons';
import { PeekViewProvider } from '@blocksuite/affine/components/peek';
import { getCommonBoundWithRotation } from '@blocksuite/affine/global/gfx';
import {
  ActionPlacement,
  type ToolbarModuleConfig,
} from '@blocksuite/affine/shared/services';
import { GfxControllerIdentifier } from '@blocksuite/affine/std/gfx';
import { QuickToolExtension } from '@blocksuite/affine/widgets/edgeless-toolbar';
import { CommentIcon } from '@blocksuite/icons/lit';
import { html } from 'lit';

import {
  addAIChatBlock,
  getAIChatBlockBoundRightOf,
} from '../../_common/chat-actions-handle';
import { AIProvider } from '../../provider';
import type { EdgelessCopilotWidget } from '../../widgets/edgeless-copilot';
import { edgelessAIGroups } from './actions-config';

const ASK_AI_SELECTION_GAP_PX = 80;

const selectedAIChatSession = (models: readonly unknown[]) => {
  if (models.length !== 1) return null;
  const model = models[0] as {
    flavour?: string;
    props?: { rootWorkspaceId?: string; sessionId?: string };
  };
  if (model.flavour !== 'affine:embed-ai-chat' || !model.props?.sessionId) {
    return null;
  }
  return model;
};

export function setupEdgelessCopilot(widget: EdgelessCopilotWidget) {
  widget.groups = edgelessAIGroups;
}

export const aiChatBlockQuickTool = QuickToolExtension(
  'aiChatBlock',
  ({ block, gfx }) => {
    const label = I18n.t('com.streamlain.ai.chat.ask-ai');
    const createChatBlock = () => {
      const blockId = addAIChatBlock(
        block.host,
        [],
        '',
        gfx.viewport.center,
        gfx.layer.generateIndex()
      );
      if (!blockId) return;

      gfx.tool.setTool(DefaultTool);
      gfx.selection.set({ elements: [blockId], editing: false });
    };

    return {
      type: 'aiChatBlock',
      content: html`<edgeless-tool-icon-button
        aria-label=${label}
        data-testid="edgeless-ai-chat-block-button"
        .tooltip=${label}
        .tooltipOffset=${17}
        .iconContainerPadding=${6}
        .iconSize=${'24px'}
        @click=${createChatBlock}
      >
        ${CommentIcon()}
      </edgeless-tool-icon-button>`,
      menu: menu.action({
        name: label,
        prefix: CommentIcon({ width: '20px', height: '20px' }),
        select: createChatBlock,
      }),
      priority: 77,
    };
  }
);

export function edgelessToolbarAIEntryConfig(): ToolbarModuleConfig {
  return {
    actions: [
      {
        placement: ActionPlacement.Start,
        id: 'A.open-ai-chat-in-sidebar',
        score: -2,
        when(ctx) {
          return !!selectedAIChatSession(ctx.getSurfaceModels());
        },
        content: ({ host }) => {
          const label = I18n.t('com.streamlain.ai.chat.canvas.open-in-sidebar');
          const openInSidebar = () => {
            const gfx = host.std.get(GfxControllerIdentifier);
            const model = selectedAIChatSession(gfx.selection.selectedElements);
            const sessionId = model?.props?.sessionId;
            if (!sessionId) return;
            AIProvider.slots.requestOpenCanvasChatSession.next({
              workspaceId:
                model.props?.rootWorkspaceId || host.store.workspace.id,
              sessionId,
            });
          };
          return html`<edgeless-tool-icon-button
            aria-label=${label}
            data-testid="open-ai-chat-in-sidebar"
            @click=${openInSidebar}
          >
            ${CommentIcon()}
            <span class="label medium">${label}</span>
          </edgeless-tool-icon-button>`;
        },
      },
      {
        placement: ActionPlacement.Start,
        id: 'A.ai-chat-block',
        score: -1,
        when(ctx) {
          const models = ctx.getSurfaceModels();
          return (
            !ctx.host.store.readonly$.value &&
            models.length > 0 &&
            !models.some(model => model.isLocked())
          );
        },
        content: ({ host }) => {
          const createChatBlock = () => {
            const gfx = host.std.get(GfxControllerIdentifier);
            const selectedElements = gfx.selection.selectedElements;
            if (!selectedElements.length) return;
            const selectedBound = getCommonBoundWithRotation(selectedElements);
            const chatBlockBound = getAIChatBlockBoundRightOf(
              selectedBound,
              ASK_AI_SELECTION_GAP_PX / Math.max(gfx.viewport.zoom, 0.01)
            );
            const placementCenter = {
              x: chatBlockBound.x + chatBlockBound.w / 2,
              y: chatBlockBound.y + chatBlockBound.h / 2,
            };
            const blockId = addAIChatBlock(
              host,
              [],
              '',
              placementCenter,
              gfx.layer.generateIndex()
            );
            if (!blockId) return;
            gfx.viewport.setViewport(
              gfx.viewport.zoom,
              [
                chatBlockBound.x + chatBlockBound.w / 2,
                chatBlockBound.y + chatBlockBound.h / 2,
              ],
              true
            );
            requestAnimationFrame(() => {
              const target = host.std.view.getBlock(blockId);
              if (target) {
                void host.std.getOptional(PeekViewProvider)?.peek({ target });
              }
            });
          };
          return html`<edgeless-tool-icon-button
            aria-label=${I18n.t('com.streamlain.ai.chat.ask-ai')}
            data-testid="ask-ai-button"
            @click=${createChatBlock}
          >
            ${AIStarIcon}
            <span
              class="label medium"
              style="color: var(--streamlain-brand-color)"
              >${I18n.t('com.streamlain.ai.chat.ask-ai')}</span
            >
          </edgeless-tool-icon-button>`;
        },
      },
    ],
  };
}
